import { useCallback, useEffect, useRef, type FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { experiencesActions } from "@ds/modules/experiences/redux/slice";
import {
  selectExperienceById,
  selectExperiences,
  selectExperiencesError,
  selectExperiencesIsLoading,
  selectSelectedExperienceIds,
} from "@ds/modules/experiences/redux/selectors";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-width: 400px;
`;

export const DuplicateExperienceModal: FC<{ onClose: () => void; experienceId?: number; onSuccess?: () => void }> = ({
  onClose,
  experienceId: experienceIdProp,
  onSuccess,
}) => {
  const selectedExperienceId = useRootSelector(selectSelectedExperienceIds)[0];
  const experienceId = experienceIdProp ?? selectedExperienceId;
  const experience = useRootSelector(state => selectExperienceById(state, experienceId));
  const experiences = useRootSelector(selectExperiences);
  const experienceNames = experiences.map(_experience => _experience.name.trim().toLowerCase());
  const isLoading = useRootSelector(selectExperiencesIsLoading);
  const error = useRootSelector(selectExperiencesError);
  const hasSubmitted = useRef(false);
  const dispatch = useRootDispatch();

  useEffect(() => {
    if (hasSubmitted.current && !isLoading) {
      if (!error) {
        onSuccess?.();
      }
      onClose();
    }
  }, [isLoading, error, onClose, onSuccess]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: experience?.name ? `${experience.name} (Copy)` : "",
    },
  });

  const onSubmit = useCallback(
    (data: { name: string }) => {
      if (experienceId === undefined || experienceId === null) return;
      hasSubmitted.current = true;
      dispatch(experiencesActions.duplicateExperience({ experienceId, newTitle: data.name }));
    },
    [dispatch, experienceId],
  );

  // NOTE: Name uniqueness is validated against the currently loaded experiences in the Redux store
  // (typically one page of results). Names outside the current page are invisible to this check,
  // so duplicate names on other pages will only be caught by the API error response.
  const { ref, ...nameRegister } = register("name", {
    required: "Name is required",
    validate: value => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return true;
      const isDuplicate = experienceNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
      return isDuplicate ? "An experience with this name already exists" : true;
    },
  });

  const renderFooter = useCallback(
    () => (
      <>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          isDisabled={isLoading}
          onClick={onClose}
        />
        <Button
          label="Duplicate"
          ariaLabel="duplicate button"
          buttonType={ButtonType.Raised}
          icon={IconType.Duplicate}
          iconPosition="right"
          isLoading={isLoading}
          isDisabled={experienceId === undefined || experienceId === null}
          onClick={handleSubmit(onSubmit)}
        />
      </>
    ),
    [experienceId, isLoading, onClose, handleSubmit, onSubmit],
  );

  return (
    <Dialog modal visible header="Duplicate Experience" footer={renderFooter} onHide={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabelWrapper label="New Experience Name" isRequired error={errors.name}>
          <InputText
            inputRef={ref}
            placeholder="Enter a new name for the duplicated experience"
            {...nameRegister}
            value={watch("name") ?? ""}
          />
        </LabelWrapper>
      </Form>
    </Dialog>
  );
};
